"use client";

import { HeaderMain } from "@/components/header-main";
import { motion } from "motion/react";
import { startTransition, useEffect, useMemo, useRef, useState, unstable_ViewTransition as ViewTransition } from "react";
import { Timeline } from "@/components/ui/timeline";
import { WobbleCard } from "@/components/ui/wobble-card";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { HeroParallax } from "@/components/ui/hero-parallax";
import { useGSAP } from "@gsap/react";
import { Flip, MotionPathPlugin, ScrambleTextPlugin, SplitText } from "gsap/all";
import Image from "next/image";
import Link from "next/link";
import Footer from "@/components/ui/footer";

gsap.registerPlugin(ScrollTrigger, ScrambleTextPlugin);

gsap.registerPlugin(MotionPathPlugin, Flip);

ScrollTrigger.config({ ignoreMobileResize: true });

const textStaggerContainer = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 2.3, // 0.3 seconds between each child
      delayChildren: 0.1 // Small delay before children start
    }
  }
};

const textRevealVariants = {
  hidden: {
    clipPath: "inset(0 0 100% 0)", // Masks from bottom
    y: 20 // Start from below
  },
  visible: {
    clipPath: "inset(0 0 0% 0)", // Reveals from bottom to top
    y: 0,
    transition: {
      delay: 1.2,
      staggerChildren: 0.1,
      duration: 0.6
    }
  }
};

const staggerContainer = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15, // Faster stagger for cards
      delayChildren: 0.2
    }
  }
};

export default function HomePage() {
  return (
    <div className=' min-h-screen  relative w-full overflow-hidden font-bebas-neue  '>
      <div
        style={{
          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32' fill='none' stroke-width='0.2' stroke='%23FFFFFFBE'   %3e%3cpath d='M0 .5H31.5V32'/%3e%3c/svg%3e")`,
          backgroundSize: "100px 100px" // kotak 100px
        }}
        className='
    fixed   w-screen min-h-screen  
    bg-gradient-to-b 
    from-zinc-100 from-5% via-[#61595900] via[50%] to-zinc-100 to-95%
    dark:from-#000000 dark:from-20% dark:via-transparent darK:via[50%] dark:to-black dark:to-95%  pointer-events-none'
      />
      <div
        className='
          fixed bg-transparent inset-0 w-screen h-screen bg-radial-[at_50%_50%] from-[#fd111100] to-zinc-100 to-50%  dark:via-[#00000000] dark:via-10% dark:to-black dark:to-80%    '
      />
      <HeaderMain />

      <div className='md:py-16 '>
        <motion.div className='@container mx-auto   px-10 md:p-0  min-h-[90vh] space-y-8 flex flex-col ' variants={staggerContainer} initial='hidden' animate='visible'>
          <motion.div className='w-full h-full flex gap-5 flex-col  py-20' variants={textStaggerContainer} initial='hidden' animate='visible'>
            <div className=' overflow-hidden md:px-15 place-center  '>
              <motion.h1 className='md:text-[17rem] text-[27.5vw]  font-bold text-foreground font-surgena  ' variants={textRevealVariants}>
                HUCO
              </motion.h1>
            </div>

            <div className=' overflow-hidden border md:p-10   h-full w-full flex-auto justify-start  flex items-start md:px-20'>
              <motion.p
                className='md:text-xs md:max-w-2xl backdrop-blur-3xl p-5  md:p-20  font-roboto-mono tracking-widest   flex  text-justify  w-full h-full relative max-w-96 mx-auto container text-sm text-neutral-500'
                variants={textRevealVariants}
              >
                Halo! Saya [HILMAN], seorang Frontend Developer yang berfokus pada membangun antarmuka web yang modern, responsif, dan interaktif.
              </motion.p>
            </div>
          </motion.div>
          <div className=' h-fit flex-1/2 flex items-center justify-center'>
            <CircleWithTypewriter />
          </div>
          <div className='  font-bebas-neue  px-10 md:p-5  justify-between w-full  flex text-xs border bg-black backdrop-blur-3xl h-fit'>
            <p className='w-24 text-justify  leading-none'>Lorem ipsum dolor, sit amet consectetur adipisicing elit.r!</p>
            <p className='w-24 leading-none text-justify'>Lorem ipsum dolor sit amet consectetur adipisicing elit.?</p>
          </div>
        </motion.div>
        <div className='min-h-screen  w-full'>
          <div className='absolute left-0 top-0 w-full h-full '>
            <SvgAnimation />
          </div>
          <PinText />
        </div>
        <HeroParallaxDemo />

        {/* <WobbleCardDemo /> */}
        {/* <TimelineDemo /> */}
      </div>
      <TextTransition />
      <ScrollPortalGSAP />
      <Footer id='contact' />
    </div>
  );
}

function SvgAnimation() {
  const pathRef = useRef<SVGPathElement | null>(null);
  const cursorRef = useRef<SVGCircleElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  useGSAP(
    () => {
      const path = pathRef.current;
      const cursor = cursorRef.current;

      if (!path || !cursor) return;
      const length = path.getTotalLength();

      gsap.set(path, {
        strokeDasharray: `${length / 5}, ${length / 10}`,
        strokeDashoffset: length,
        strokeLinecap: "round"
      });

      gsap
        .timeline({
          scrollTrigger: {
            trigger: svgRef.current,
            start: "top top", // mulai ketika svg sampai atas
            end: "bottom+=20% top", // panjang animasi scroll
            // markers: true,
            scrub: true,
            pin: true // biar svg stay di layar
          }
        })
        .to(cursor, { strokeDashoffset: 0, opacity: 1, duration: 0.1, fill: "red", ease: "circ.in" })
        .to(path, { strokeDashoffset: 0, opacity: 1, ease: "none" })
        .to(
          cursor,
          {
            opacity: 1,
            fill: "white",
            motionPath: {
              path: path,
              align: path,
              alignOrigin: [0.5, 0.5]
            },
            ease: "none"
          },
          "<"
        )
        .to(
          cursor,
          {
            autoAlpha: 1,
            scale: 5,
            duration: 0.1,
            ease: "circ.out",
            color: "white",
            fill: "white"
          },
          ">"
        )
        .to(path, {
          strokeDashoffset: 0,
          ease: "bounce.out",
          duration: 0.1,
          opacity: 1
        });
    },
    { scope: svgRef }
  );

  return (
    <>
      <svg ref={svgRef} xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 600' className='w-full h-[100vh]  -z-10 md:h-screen '>
        <path
          ref={pathRef}
          d='M135.970155,65.961116c252.064939-68.356594,222.15893,183.708344,81.173456,271.717459s-278.55312-158.074622-90.572488-100.825975q187.980632,57.248647,317.455036,270.814349'
          fill='none'
          style={{ opacity: 0 }}
          stroke='lab(63.39 0.76 0.25 / 0.9)'
          strokeWidth='5'
        />
        <circle ref={cursorRef} r='8' fill='none' style={{ opacity: 0 }} />
      </svg>
    </>
  );
}
const PinText = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hoveredProjectIndex, setHoveredProjectIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      // const texts = gsap.utils.toArray<HTMLElement>(".parallax-text");

      // texts.forEach((text, i) => {
      //   gsap.to(text, {
      //     yPercent: i === 0 ? -200 : i === 1 ? -500 : i === 2 ? -700 : -1000,
      //     ease: "none",
      //     scrollTrigger: {
      //       trigger: text,
      //       start: "top bottom",
      //       end: "bottom top",
      //       markers: true,
      //       scrub: true
      //     }
      //   });
      // });
      gsap.utils.toArray<HTMLElement>(".parallax-text").forEach((el, i) => {
        gsap.fromTo(
          el,
          { yPercent: -200, opacity: 0, gap: "200px" },
          {
            yPercent: -i * -550, // beda speed tiap elemen
            opacity: 1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: el,

              start: "top 80%",
              end: "bottom top",
              scrub: true
            }
          }
        );
        gsap.to(el, {
          duration: 1.5,
          scrambleText: {
            text: el.textContent, // teks asli
            chars: "upperAndLowerCase", // huruf acak
            revealDelay: 0.2,
            speed: 0.5
          },
          scrollTrigger: {
            trigger: el,
            // markers: true,
            start: "top 80%",
            end: "bottom top",
            toggleActions: "play none none reverse"
          },
          delay: i * 0.2 // supaya teks muncul bergiliran
        });
      });
      const mm = gsap.matchMedia();
      mm.add("(max-width: 639px)", () => {
        gsap.set(".right-side", {
          scale: 0.7, // kecilkan scale
          scrollTrigger: {
            trigger: ".right-side",
            pinnedContainer: containerRef.current,
            scrub: 0.01,
            pin: true,
            pinSpacing: true,
            // markers: true,
            start: "top 20%"
          }
        });
      });
      mm.add("(min-width: 640px)", function () {
        gsap.set(".right-side", {
          scale: 1, // normal
          scrollTrigger: { trigger: ".right-side", pinnedContainer: containerRef.current, scrub: 0.01, pin: true, pinSpacing: false, start: "top 20%", end: "+=200% top" }
        });
      });
    },
    { scope: containerRef }
  );
  const projects = ["WEB 3D", "WEDDING", "E-Commerce", "Photo Studio", "Banking", "Music 3D", "PORTFOLIO"];

  const images = ["/image/sc/vp-ip-homepage.jpg", "/image/sc/vp-ip-homepage1.jpg", "/image/sc/vp-ip-homepage2.jpg"];

  useGSAP(() => {}, { scope: containerRef });
  return (
    <motion.div
      ref={containerRef}
      className='min-h-screen  flex 2xl:items-start 2xl:justify-start  xl:w-full items-center justify-center p-8'
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ amount: 0.3 }}
      transition={{ duration: 0.8 }}
    >
      <div className='md:max-w-7xl  md:min-h-[200vh] md:grid md:grid-rows-2 w-full grid grid-cols-1 lg:grid-cols-2 gap-16 items-center'>
        {/* Left side - Overlapping Cards */}
        <div className='relative h-[600px]  flex items-center justify-center'>
          <motion.div className='relative w-full max-w-md' initial={{ opacity: 0, scale: 0.8, x: -100 }} whileInView={{ opacity: 1, scale: 1, x: 0 }} viewport={{ amount: 0.5 }} transition={{ duration: 0.8, ease: "easeOut" }}>
            {images.map((image, index) => (
              <motion.div
                key={index}
                className='absolute inset-0 cursor-pointer'
                style={{
                  clipPath:
                    index === 0
                      ? "path('M 50 0 L 350 0 Q 600 0 600 30 L 600 200 Q 600 230 350 230 L 200 230 Q 170 230 170 200 L 170 100 Q 170 70 140 70 L 80 70 Q 50 70 50 40 L 50 30 Q 50 0 80 0 Z')"
                      : index === 1
                      ? "path('M 0 50 L 0 20 Q 0 0 20 0 L 280 0 Q 310 0 310 30 L 310 350 Q 310 600 280 600 L 150 600 Q 120 600 120 350 L 120 250 Q 120 220 90 220 L 30 220 Q 0 220 0 190 L 0 50 Z')"
                      : "path('M 0 50 L 0 20 Q 0 0 20 0 L 280 0 Q 310 0 310 30 L 310 350 Q 310 600 280 600 L 150 600 Q 120 600 120 350 L 120 250 Q 120 220 90 220 L 30 220 Q 0 220 0 190 L 0 50 Z')"
                }}
                initial={{
                  x: index * 20 - 50,
                  y: index * 30 + 50,
                  scale: 0.8,
                  rotate: index * 2 + 10,
                  opacity: 0
                }}
                whileInView={{
                  x: index * 100,
                  y: index * -160,
                  scale: 1 - index * 0.05,
                  rotate: index * 20,
                  opacity: 1
                }}
                viewport={{ amount: 0.3 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 25,
                  delay: index * 0.2
                }}
                whileTap={{
                  scale: 1.05,
                  rotate: 0,
                  x: index * 10,
                  y: index * 15,
                  zIndex: 10
                }}
                whileHover={{
                  scale: 1.05,
                  rotate: 0,
                  x: index * 10,
                  y: index * 15,
                  zIndex: 10
                }}
                onHoverStart={() => setHoveredIndex(index)}
                onHoverEnd={() => setHoveredIndex(null)}
              >
                <div
                  className='w-full h-full bg-cover bg-center rounded-3xl shadow-2xl'
                  style={{
                    backgroundImage: `url(${image})`,
                    width: "380px",
                    height: "280px"
                  }}
                >
                  <div className='w-full h-full bg-black/20 rounded-3xl flex items-end p-6'>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{
                        opacity: hoveredIndex === index ? 1 : 0,
                        y: hoveredIndex === index ? 0 : 20
                      }}
                      transition={{ duration: 0.3 }}
                      className='text-white'
                    >
                      <h3 className='text-xl font-bold mb-2'>Project {index + 1}</h3>
                      <p className='text-sm opacity-90'>Creative portfolio showcase</p>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Right side - Project List */}

        <div className=' right-side text-xs  py-20  space-y-0 bg-[#E5FFBF] md:p-20 px-5 relative '>
          <svg
            className='absolute top-0 left-0 w-full h-full'
            preserveAspectRatio='none'
            id='eEKWQUpRSmG1'
            xmlns='http://www.w3.org/2000/svg'
            xmlnsXlink='http://www.w3.org/1999/xlink'
            viewBox='0 0 300 300'
            shapeRendering='geometricPrecision'
            textRendering='geometricPrecision'
            project-id='c0f5bdb30b5244a3a88bf42f17f5d9f3'
            export-id='cd646aa5e99441a5b5f949fdc6c8b8aa'
          >
            <path
              d='M-0.93215,0.00004c0,0,298.164516-.720938,300,0v299.999155h-300c0,0,0-299.10897,0-299.10897v-.890185ZM10.858886,289.999223h59.629441l9.93824-30.133663h139.135362l9.93824,30.133663h59.629441v-279.999228c0,0-59.629441,0-59.629441,0s-9.93824,19.999945-9.93824,19.999945-139.135362,0-139.135362,0L70.488327,9.999995h-59.629441v279.999228Z'
              transform='translate(.93215-.00004)'
              strokeWidth='0.6'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>

          <motion.div initial={{ opacity: 0, x: 100 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.5 }} transition={{ duration: 0.8, delay: 0.2 }} className='mb-8'>
            <h2 className='text-5xl font-bold text-gray-900 mb-4'>[ WORK ]</h2>
            <div className='flex justify-end'>
              <span className='text-lg font-bold text-gray-900'>MENU</span>
            </div>
          </motion.div>

          <div className='relative '>
            <motion.div
              className='absolute  inset-x-0 bg-black rounded-lg -mx-4'
              style={{
                height: "50px" // Height of each project item
              }}
              initial={{ opacity: 0, y: 0 }}
              animate={{
                opacity: hoveredProjectIndex !== null ? 1 : 0,
                y: hoveredProjectIndex !== null ? hoveredProjectIndex * 50 : 0 // 60px height + 4px gap
              }}
              transition={{
                type: "spring",
                stiffness: 400,
                damping: 30,
                opacity: { duration: 0.2 }
              }}
            />

            {projects.map((project, index) => (
              <motion.div
                key={project}
                className='relative font-roboto-mono cursor-pointer py-3 px-4 -mx-4 rounded-lg transition-all duration-300 z-10'
                style={{ height: "50px" }} // Fixed height for consistent spacing
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.8 }}
                transition={{ duration: 0.6, delay: 0.4 + 0.1 * index }}
                onTapStart={() => setHoveredProjectIndex(index)}
                onTapCancel={() => setHoveredProjectIndex(null)}
                onHoverStart={() => setHoveredProjectIndex(index)}
                onHoverEnd={() => setHoveredProjectIndex(null)}
              >
                <motion.h3
                  className={`md:text-2xl text-normal font-bold transition-all duration-300 ${
                    hoveredProjectIndex === index ? "text-white" : index === 1 || index === 3 || index == 5 ? " text-black px-3 py-1 inline-block" : "text-orange-300"
                  }`}
                  whileHover={{ scale: 1.02 }}
                >
                  {project}
                </motion.h3>
              </motion.div>
            ))}
          </div>
        </div>
        <div className='md:block hidden min-h-screen    flex-col items-center justify-center text-white '>
          <h1 className='parallax-text  font-bold  border border-#23d300-300  text-9xl'>Creative</h1>
          <h1 className='parallax-text text-8xl font-bold md:px-30  '>Design</h1>
          <h1 className='parallax-text text-6xl font-bold  '>Studio</h1>
          <h1 className='parallax-text text-5xl font-bold px-72'>2025</h1>
        </div>
      </div>
    </motion.div>
  );
};

const CircleWithTypewriter = () => {
  const text = "SCROLL DOWN !";
  const [displayedText, setDisplayedText] = useState("");
  const [index, setIndex] = useState(0);
  const [startTyping, setStartTyping] = useState(false);

  // efek typewriter
  useEffect(() => {
    if (startTyping && index < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText((prev) => prev + text[index]);
        setIndex(index + 1);
      }, 220);
      return () => clearTimeout(timeout);
    }
  }, [index, text, startTyping]);
  const variants = {
    hidden: { x: "0", scale: 0.8, opacity: 1 },
    visible: { x: "10", scale: 1, opacity: 1 }
  };
  return (
    <motion.h1
      variants={variants}
      initial='hidden'
      animate='visible'
      transition={{ duration: 1, ease: "easeInOut", delay: 1 }}
      onAnimationComplete={() => setStartTyping(true)}
      className='text-center items-end flex place-content-center justify-center w-full   h-full webkit-sm md:webkit-md font-bold text-3xl md:text-9xl '
    >
      {displayedText}
      {startTyping && <span className='animate-caret-blink  relative -top-1 text-white'>|</span>}
    </motion.h1>
  );
};
const TextTransition = () => {
  const containerRef = useRef(null);
  const topTextRef = useRef(null);
  const midTextRef = useRef(null);
  const mid1TextRef = useRef(null);
  const bottomTextRef = useRef(null);
  useGSAP(
    () => {
      ScrollTrigger.normalizeScroll(true);
      // Pin section
      ScrollTrigger.create({
        trigger: containerRef.current,
        start: "top top",
        end: "+=255%",
        scrub: 0.01,
        pin: true
      });

      // IMMERSIVE atas (datang dari jauh lalu terbang ke belakang)
      gsap.fromTo(
        topTextRef.current,
        { z: 0, scaleX: 1.5, scaleY: 5, opacity: 1, rotationX: -90, perspective: 1000 },
        {
          z: 30000,
          scaleX: 1.5,
          scaleY: 5,
          opacity: 1,

          rotationX: -90,

          ease: "none",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top top",
            end: "+=300%",
            scrub: 0.5
          }
        }
      );
      // Teks tengah (mendekat seperti kamera zoom)
      gsap.fromTo(
        midTextRef.current,
        { z: 0, scaleX: 1, opacity: 1 },
        {
          z: 8000,
          scaleX: 1,
          opacity: 0,

          ease: "none",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top top",
            end: "+=300%",
            // markers: true,
            scrub: 0.5
          }
        }
      );
      gsap.fromTo(
        mid1TextRef.current,
        { z: -2000, scaleX: 1, opacity: 0 },
        {
          z: 2000,
          scaleX: 1,
          opacity: 1,
          ease: "none",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top top",
            // markers: true,
            end: "+=300%",
            scrub: 0.5
          }
        }
      );
      // EXPERIENCES bawah (muncul dari belakang)
      gsap.fromTo(
        bottomTextRef.current,
        { z: 0, scaleX: 1.5, scaleY: 5, opacity: 1, rotationX: 90, perspective: 1000 },
        {
          z: 30000,
          scaleX: 1.5,
          scaleY: 5,
          opacity: 1,

          rotationX: 90,

          ease: "none",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top top",
            end: "+=300%",
            scrub: 0.5
          }
        }
      );
    },
    { scope: containerRef }
  );
  return (
    <div
      ref={containerRef}
      className=' !max-w-none !mx-0 !w-full min-h-screen backdrop-brightness-200 flex items-center justify-center relative overflow-hidden text-white'
      style={{
        perspective: "1500px"
      }}
    >
      <div
        ref={topTextRef}
        className='absolute flex bg-gradient-to-b from-black via-transparent to-white   flex-col gap-16 top-0 text-[20vh]  font-bold opacity-50'
        style={{
          transformOrigin: "top center",
          perspective: "900px"
        }}
      >
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
      </div>
      <div></div>
      <h2 ref={midTextRef} className=' absolute  text-center text-[6vw] font-bold text-[#E5FFBF]  z-10'>
        AND I&apos;M NOT AFRAID
      </h2>
      <h2 ref={mid1TextRef} className='text-[6vw] font-bold text-[rgb(229,255,191)] absolute z-10'>
        Lets Touch iT
      </h2>
      <div
        ref={bottomTextRef}
        className='absolute flex  flex-col gap-16 bottom-0 bg-gradient-to-b from-black  via-transparent to-white  text-[20vh] bg-white text-black  font-bold opacity-50'
        style={{
          transformOrigin: "bottom center",
          perspective: "900px"
        }}
      >
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
        <p>IMMERSIVE</p>
      </div>
    </div>
  );
};
function ScrollPortalGSAP() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      gsap.fromTo(
        boxRef.current,
        {
          width: "2vw", // size-96 awal
          height: "2vw",
          ease: "power1.in",
          reversed: true
        },
        {
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top top", // mulai saat elemen masuk dari atas
            end: "bottom top", // selesai saat elemen keluar ke bawah
            scrub: true,
            // markers: true,
            pin: true,
            once: false
          },
          width: "100vw",
          height: "100vh"
        }
      );
    },
    { scope: sectionRef }
  );

  return (
    <div ref={sectionRef} className=' container mx-auto min-h-screen flex items-center justify-center '>
      <motion.div
        ref={boxRef}
        className='md:p-10  p-2 border backdrop-blur-md rounded-lg z-10'
        animate={{
          boxShadow: ["0 0 5px #000000, 0 0 10px #FFFF0A09", "0 0 20px #7C7C7CFD, 0 0 190px #FEFFFED3", "0 0 5px #020000, 0 0 10px #360707"]
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        {/* Portal wrapper */}
        <motion.div
          className='
        w-full h-full overflow-hidden border-4 border-[#ffb004] relative
        [mask:conic-gradient(at_20px_20px,#0000_75%,#000_0)_0_0/calc(100%_-_20px)_calc(100%_-_20px),conic-gradient(#000_0_0)_content-box]
        -webkit-mask-image: conic-gradient(at 20px 20px,#0000 75%,#000 0);
      '
          initial='hidden'
          whileInView='visible'
          viewport={{ once: true, amount: 0.6 }} // amount: 0.3 artinya 30% elemen terlihat baru animasi jalan
          variants={{
            hidden: { opacity: 0, y: 40 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
          }}
        >
          {/* Background image */}
          <motion.div initial={{ scale: 1.1 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 1.2, ease: "easeOut" }}>
            <Image src='/image/porto_page.png' fill unoptimized alt='me' className='absolute inset-0 w-full h-full object-cover object-center' />
          </motion.div>
          {/* Overlay gradient */}
          <motion.div className='absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent' initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1 }} />

          {/* Teks */}
          <motion.div className='absolute bottom-6 left-6 text-white z-10' initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false }} transition={{ duration: 0.8, delay: 0.5 }}>
            <Link href={"/portofolio"} className='text-3xl font-bold hover:text-[#ffdb8d] text-[#FFB004]'>
              Go To
            </Link>
            <p className=' text-white/80 font-light font-raleway text-xs'>My Portfolio</p>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}

function HeroParallaxDemo() {
  return <HeroParallax products={products} />;
}
const products = [
  {
    title: "Go To Photo Studio",
    link: "/templates/photostudio",
    thumbnail: "/image/sc/vp-dc-homepage.jpg"
  },

  {
    title: "Go To Photo Studio",
    link: "/templates/photostudi",
    thumbnail: "/image/sc/vp-dc-homepage1.jpg"
  },

  {
    title: "Go To Photo Studio",
    link: "/templates/photostudio",
    thumbnail: "/image/sc/vp-dc-homepage2.jpg"
  },

  {
    title: "Go To Photo Studio",
    link: "/templates/photostudio",
    thumbnail: "/image/sc/vp-dc-homepage3.jpg"
  },

  {
    title: "Go To Photo Studio",
    link: "/templates/photostudio",
    thumbnail: "/image/sc/vp-dc-homepage4.jpg"
  },

  {
    title: "Go To",
    link: "/templates/bankings",
    thumbnail: "/image/sc/vp-dc-homepage5.jpg"
  },

  {
    title: "Go To Bankings",
    link: "/templates/bankings",
    thumbnail: "/image/sc/vp-dc-homepage6.jpg"
  },

  {
    title: "Go To Bankings",
    link: "/templates/bankings",
    thumbnail: "/image/3d_music_mobile.png"
  },

  {
    title: "Go To loading",
    link: "/templates/bankings",
    thumbnail: "/image/sc/vp-ip-loading.jpg"
  },

  {
    title: "Msuci 3D",
    link: "/image/3d_music.png",
    thumbnail: "/image/3d_music.png"
  }
];
